
import React, { useState, useEffect } from 'react';
import { commonOverlayStyle } from './overlayStyles';

const OfflineOverlay = ({ job_interview_id }) => {
  const [animateIcon, setAnimateIcon] = useState(false);
  const [expandedTip, setExpandedTip] = useState(null);

  useEffect(() => {
    // Create pulsing animation effect
    const interval = setInterval(() => {
      setAnimateIcon(prev => !prev);
    }, 1500);
    
    return () => clearInterval(interval);
  }, []);

  const troubleshootingTips = [
    {
      id: 1,
      title: 'Check Wi-Fi Connection',
      content: 'Ensure your Wi-Fi is turned on and connected to the correct network. Try turning Wi-Fi off and on again.'
    },
    {
      id: 2,
      title: 'Try Mobile Data',
      content: 'If Wi-Fi isn\'t working, try switching to mobile data temporarily to continue your interview.'
    },
    {
      id: 3,
      title: 'Restart Router',
      content: 'Power cycle your router by unplugging it for 30 seconds, then plugging it back in. Wait 1-2 minutes for it to fully restart.'
    },
    {
      id: 4,
      title: 'Check Other Devices',
      content: 'Test if other devices can connect to the internet to determine if the issue is with your device or the network.'
    }
  ];

  const toggleTip = (id) => {
    if (expandedTip === id) {
      setExpandedTip(null);
    } else {
      setExpandedTip(id);
    }
  };

  return (
    <div style={{
      ...commonOverlayStyle,
      background: 'linear-gradient(135deg, rgba(23, 24, 32, 0.95) 0%, rgba(34, 37, 75, 0.95) 100%)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      padding: '20px'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '500px',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        padding: '30px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: 'rgba(255, 59, 48, 0.1)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '20px',
          transition: 'all 0.5s ease',
          transform: animateIcon ? 'scale(1.1)' : 'scale(1)',
          boxShadow: animateIcon ? '0 0 20px rgba(255, 59, 48, 0.5)' : 'none'
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" />
            <path d="M16 8L18 6" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" />
            <path d="M19 12H17" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" />
            <path d="M12 12L12 8" stroke="#FF3B30" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>

        {/* Title */}
        <h1 style={{
          color: '#FF3B30',
          fontSize: '28px',
          fontWeight: '700',
          margin: '0 0 10px 0',
          textAlign: 'center'
        }}>
          Connection Lost
        </h1>

        {/* Subtitle */}
        <p style={{
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: '16px',
          marginBottom: '25px',
          textAlign: 'center',
          lineHeight: '1.5'
        }}>
          Your internet connection appears to be offline. We'll automatically reconnect you to your interview when your connection is restored.
        </p>

        {/* Divider */}
        <div style={{
          width: '80%',
          height: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          margin: '10px 0 20px 0'
        }} />

        {/* Troubleshooting Section */}
        <h2 style={{
          color: 'rgba(255, 255, 255, 0.9)',
          fontSize: '18px',
          fontWeight: '600',
          marginBottom: '15px',
          textAlign: 'center'
        }}>
          Try these troubleshooting steps:
        </h2>

        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          {troubleshootingTips.map(tip => (
            <div key={tip.id} style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              overflow: 'hidden',
              transition: 'all 0.3s ease'
            }}>
              <div 
                onClick={() => toggleTip(tip.id)}
                style={{
                  padding: '12px 15px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  backgroundColor: expandedTip === tip.id ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  transition: 'background-color 0.3s ease'
                }}
              >
                <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: '500' }}>{tip.title}</span>
                <span style={{ 
                  transform: expandedTip === tip.id ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}>
                  ▼
                </span>
              </div>
              {expandedTip === tip.id && (
                <div style={{
                  padding: '0 15px 15px 15px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {tip.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Status Indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginTop: '25px',
          padding: '10px 15px',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '30px'
        }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: '#FF3B30',
            marginRight: '10px',
            boxShadow: '0 0 10px #FF3B30',
            animation: 'pulse 1.5s infinite'
          }} />
          <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
            Attempting to reconnect...
          </span>
        </div>

        <style jsx>{`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OfflineOverlay;